import { useEffect, useId, useState, type FC } from "react";
import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { Textarea } from "@/ds/shadcn/components/ui/textarea";
import { Field, FieldDescription, FieldLabel } from "@/ds/shadcn/components/ui/field";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import type { ProblemResponse } from "@/lib/http";
import type { SubscriptionModel } from "@/features/common/api/useSubscription";
import { useSubscriptionDelete } from "@/features/subscriptions/api/useSubscriptionDelete";
import { formatSubscriptionDate } from "@/features/subscriptions/SubscriptionLogic";

const MINIMUM_REASON_LENGTH = 4;
const MAXIMUM_REASON_LENGTH = 500;

export interface ModalCancelSubscriptionProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subscription: SubscriptionModel;
}

export const ModalCancelSubscription: FC<ModalCancelSubscriptionProps> = props => {
	const {
		open,
		onOpenChange,
		subscription
	} = props;

	const subscriptionDelete = useSubscriptionDelete();
	useNotifyMutationSuccess(subscriptionDelete, "Subscription cancelled");

	useEffect(() => {
		if (open) {
			subscriptionDelete.reset();
		}
	}, [open]);

	useEffect(() => {
		if (subscriptionDelete.isSuccess) {
			onOpenChange(false);
		}
	}, [subscriptionDelete.isSuccess]);

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={subscriptionDelete.isPending}>
			<ModalHeader>
				<ModalTitle>Cancel Subscription</ModalTitle>
				<ModalDescription>
					{
						subscription.activeSubscription
							? `Your subscription won't renew. You'll keep premium access until ${formatSubscriptionDate(subscription.activeSubscription.expiryDate)}.`
							: "Your subscription won't renew."
					}
				</ModalDescription>
			</ModalHeader>

			<ModalCancelSubscriptionContent
				isPending={subscriptionDelete.isPending}
				error={subscriptionDelete.error}
				onCancel={() => onOpenChange(false)}
				onSubmit={cancellationReason => subscriptionDelete.mutate({ cancellationReason })} />
		</Modal>
	);
};

interface ModalCancelSubscriptionContentProps {
	isPending: boolean;
	error: ProblemResponse | null;
	onCancel: () => void;
	onSubmit: (cancellationReason: string) => void;
}

const ModalCancelSubscriptionContent: FC<ModalCancelSubscriptionContentProps> = props => {
	const {
		isPending,
		error,
		onCancel,
		onSubmit
	} = props;

	const id = useId();
	const [cancellationReason, setCancellationReason] = useState("");
	const trimmedCancellationReason = cancellationReason.trim();
	const canSubmit = trimmedCancellationReason.length >= MINIMUM_REASON_LENGTH && !isPending;

	return (
		<>
			<ModalBody>
				<div className="flex flex-col gap-3">
					<Field>
						<FieldLabel htmlFor={id}>Could you share with us why you&apos;re cancelling?</FieldLabel>
						<Textarea
							id={id}
							value={cancellationReason}
							maxLength={MAXIMUM_REASON_LENGTH}
							disabled={isPending}
							onChange={e => setCancellationReason(e.currentTarget.value)} />
						<FieldDescription>At least {MINIMUM_REASON_LENGTH} characters.</FieldDescription>
					</Field>

					<ErrorDisplay error={error} />
				</div>
			</ModalBody>

			<ModalFooter>
				<Button variant="outline" disabled={isPending} onClick={onCancel}>
					Back
				</Button>
				<Button variant="destructive" disabled={!canSubmit} onClick={() => onSubmit(trimmedCancellationReason)}>
					{isPending ? <Spinner /> : "Cancel Subscription"}
				</Button>
			</ModalFooter>
		</>
	);
};
